/**
 * Home → APP Plus state (D7, wireframe 3+4): the user's ACOs with the
 * two-field Register ACO dialog. Clicking Open selects the ACO and enters the
 * APP Plus module. Registration mirrors the practice flow: client owners (and
 * admins) register; the CMS ACO ID is globally unique like a practice TIN.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Landmark, Plus, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';
import { useAco, type Aco } from '@/contexts/AcoContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';

interface AcoWithMeta extends Aco {
  roster_clinicians?: number;
  roster_tins?: number;
  config_years?: number[];
}

export function AcoList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { clients } = useAuth();
  const { acos, selectAco, refresh, loading } = useAco();
  const { programYear } = useProgramYear();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [acoIdCms, setAcoIdCms] = useState('');
  const [acoName, setAcoName] = useState('');
  const [clientId, setClientId] = useState<string>(clients.length === 1 ? String(clients[0].id) : '');
  const [saving, setSaving] = useState(false);

  const idValid = /^A\d{4}$/.test(acoIdCms.trim().toUpperCase());
  const canSubmit = idValid && acoName.trim().length > 0 && clientId !== '';
  const clientById = useMemo(() => new Map(clients.map(c => [c.id, c])), [clients]);

  const handleRegister = async () => {
    setSaving(true);
    try {
      const created = await apiClient.post('/api/acos', {
        client_id: Number(clientId),
        name: acoName.trim(),
        aco_id_cms: acoIdCms.trim().toUpperCase(),
      });
      await refresh();
      setDialogOpen(false);
      setAcoIdCms(''); setAcoName('');
      toast({ title: 'ACO registered', description: `${created.name} (${created.aco_id_cms})` });
    } catch (e) {
      toast({
        title: 'Registration failed',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpen = (aco: Aco) => {
    selectAco(aco.id);
    navigate('/programs/app-plus');
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Loading your ACOs…</div>;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Your ACOs</h1>
          <p className="text-muted-foreground mt-2">APP Plus (MSSP) reporting entities.</p>
        </div>
        <Button className="h-11 px-4" onClick={() => setDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Register ACO
        </Button>
      </div>

      {acos.length === 0 ? (
        <div className="rounded-lg border bg-card py-16 text-center">
          <Landmark className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
          <p className="text-lg font-medium">No ACOs registered yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Register your MSSP ACO with its CMS ACO ID to start APP Plus reporting.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {(acos as AcoWithMeta[]).map(aco => (
            <Card
              key={aco.id}
              className="group cursor-pointer transition-all duration-200 hover:shadow-md hover:bg-accent/20"
              onClick={() => handleOpen(aco)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <Landmark className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{aco.name}</CardTitle>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="font-bold text-primary">{aco.aco_id_cms}</span>
                        <Badge variant="secondary"
                          className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80 px-2.5 py-0.5 text-xs font-medium">
                          APM Entity
                        </Badge>
                        {clients.length > 1 && clientById.get(aco.client_id) && (
                          <span className="text-xs text-muted-foreground">
                            {clientById.get(aco.client_id)!.client_alias}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost"
                    className="opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={(e) => { e.stopPropagation(); handleOpen(aco); }}>
                    Open <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0 pl-[3.5rem] text-sm text-muted-foreground">
                {(aco.roster_clinicians ?? 0) > 0
                  ? <>Roster: {aco.roster_clinicians} clinicians · {aco.roster_tins} TINs</>
                  : <>No roster uploaded for PY {programYear}</>}
                {(aco.config_years?.length ?? 0) > 0 && (
                  <span className="ml-2">
                    · Configured: {aco.config_years!.map(y => `PY ${y}`).join(', ')}
                  </span>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Register ACO</DialogTitle>
            <DialogDescription>
              Two fields — everything else (track, year settings) lives in APP Plus → Configuration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {clients.length > 1 && (
              <div className="space-y-1.5">
                <Label>Organization</Label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.client_alias}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>CMS ACO ID</Label>
              <Input
                placeholder="A0000"
                value={acoIdCms}
                onChange={(e) => setAcoIdCms(e.target.value.toUpperCase())}
                maxLength={5}
              />
              <p className="text-xs text-muted-foreground">
                From your QPP portal · "A" + 4 digits · must not already be registered
              </p>
              {acoIdCms.length === 5 && !idValid && (
                <p className="text-xs text-red-600">Format must be A followed by 4 digits (e.g. A5495).</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>ACO Name</Label>
              <Input
                placeholder="Legal entity name"
                value={acoName}
                onChange={(e) => setAcoName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button disabled={!canSubmit || saving} onClick={handleRegister}>
              {saving ? 'Registering…' : 'Register'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
